import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  Snackbar, 
  Link, 
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getCookieConsent, getCookieSettings, setAnalyticsCookies, setMarketingCookies } from '../utils/cookieManager';

interface CookieSettings {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const CookieConsent: React.FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cookieSettings, setCookieSettings] = useState<CookieSettings>({
    necessary: true, // Always required
    analytics: true,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already made a cookie choice
    const cookieConsent = getCookieConsent();
    if (!cookieConsent) {
      // Show the banner after a short delay
      const timer = setTimeout(() => {
        setOpen(true);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // If consent exists, apply the cookie settings
      const settings = getCookieSettings();
      if (settings.analytics) {
        setAnalyticsCookies();
      }
      if (settings.marketing) {
        setMarketingCookies();
      }
    }
  }, []);

  const handleAcceptAll = () => {
    // Save full consent to localStorage
    localStorage.setItem('cookieConsent', 'full');
    localStorage.setItem('cookieSettings', JSON.stringify({
      necessary: true,
      analytics: true,
      marketing: true
    }));
    
    // Apply cookie settings
    setAnalyticsCookies();
    setMarketingCookies();
    
    setOpen(false);
  };

  const handleCustomize = () => {
    // Load saved settings if available
    const savedSettings = getCookieSettings();
    setCookieSettings(savedSettings);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCookieSettings({
      ...cookieSettings,
      [event.target.name]: event.target.checked,
    });
  };

  const handleSavePreferences = () => {
    // Save custom preferences
    localStorage.setItem('cookieConsent', 'custom');
    localStorage.setItem('cookieSettings', JSON.stringify(cookieSettings));
    
    // Apply cookie settings based on user preferences
    if (cookieSettings.analytics) {
      setAnalyticsCookies();
    }
    if (cookieSettings.marketing) {
      setMarketingCookies();
    }
    
    setDialogOpen(false);
    setOpen(false);
  };

  return (
    <>
      {/* Cookie Consent Banner */}
      <Snackbar
        open={open}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 0, sm: 20 } }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 2,
            maxWidth: 600,
            width: '100%',
            mx: 'auto',
          }}
        >
          <Typography variant="h6" gutterBottom>
            {t('cookieConsent.title')}
          </Typography>
          <Typography variant="body2" paragraph>
            {t('cookieConsent.description')}{' '}
            <Link component={RouterLink} to="/cookies-policy">
              {t('cookieConsent.learnMore')}
            </Link>
          </Typography>
          
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
            <Button 
              size="small" 
              color="primary" 
              onClick={handleCustomize}
            >
              {t('cookieConsent.customize')}
            </Button>
            <Button 
              size="small" 
              variant="contained" 
              color="primary" 
              onClick={handleAcceptAll}
            >
              {t('cookieConsent.acceptAll')}
            </Button>
          </Stack>
        </Paper>
      </Snackbar>

      {/* Customize Settings Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        aria-labelledby="cookie-settings-dialog"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="cookie-settings-dialog">
          {t('cookieConsent.settingsTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText paragraph>
            {t('cookieConsent.settingsDescription')}
          </DialogContentText>
          
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox 
                  checked={cookieSettings.necessary} 
                  name="necessary" 
                  disabled // Cannot be unchecked
                />
              }
              label={
                <Box>
                  <Typography variant="subtitle2">{t('cookieConsent.necessaryCookies')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('cookieConsent.necessaryDescription')}
                  </Typography>
                </Box>
              }
            />
            
            <FormControlLabel
              control={
                <Checkbox 
                  checked={cookieSettings.analytics} 
                  onChange={handleChange} 
                  name="analytics" 
                />
              }
              label={
                <Box>
                  <Typography variant="subtitle2">{t('cookieConsent.analyticsCookies')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('cookieConsent.analyticsDescription')}
                  </Typography>
                </Box>
              }
            />
            
            <FormControlLabel
              control={
                <Checkbox 
                  checked={cookieSettings.marketing} 
                  onChange={handleChange} 
                  name="marketing" 
                />
              }
              label={
                <Box>
                  <Typography variant="subtitle2">{t('cookieConsent.marketingCookies')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('cookieConsent.marketingDescription')}
                  </Typography>
                </Box>
              }
            />
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSavePreferences} color="primary" variant="contained">
            {t('cookieConsent.savePreferences')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CookieConsent; 